import {Group} from '@visx/group';
import {observer} from 'mobx-react';
import {scaleThreshold} from '@visx/scale';
import {Popup, PopupProps} from 'semantic-ui-react';
import {useResizeDetector} from 'react-resize-detector';
import {syncPopperHideAttributes} from 'apstra-ui-common';
import {map, chunk, get, min, find, isUndefined, Dictionary, omit, transform} from 'lodash';
import {useState, useMemo, useEffect, useRef, useLayoutEffect, useCallback, FC, Component} from 'react';

import Hexagon from './Hexagon';
import './Honeycomb.less';

export const CELL_LABEL_PADDING = 3;
export const SUIR_PADDING = 14;

export type CellDataType = {
  id: string;
  rowId: number;
  cellId: number;
  faded: boolean;
  label: React.ReactNode | string;
  data: object;
  color: string;
};

type PopupDataType = Omit<CellDataType, 'id'> & {
  itemId: string;
  node: HTMLElement;
};

export type ItemType = Dictionary<any> & {
  id: number;
};

export type HoneycombPropsType = {
  items: ItemType[];
  valueKeyName?: string;
  containerWidth?: number;
  labelClassNames?: string;
  numberOfCellsPerRow?: number;
  cellBaseSize?: number;
  cellOffset?: number;
  idFn: (item: ItemType) => string;
  cellLabelChildrenFn: (item: ItemType) => React.ReactNode | string;
  popupContentFn: (data: PopupDataType) => React.ReactNode | string;
  openPopupOn?: 'hover' | 'click';
  highlightedPropPath?: string[];
  onHoneycombClick?: (data: Partial<CellDataType>) => void;
  colors?: string[];
  colorThresholds?: number[];
};

type PopupType = Component<PopupProps, any, any> & {
  elementRef: {
    current: HTMLElement | null;
  }
};

const makePopupData = (cellData: CellDataType, e) => ({
  ...omit(cellData, ['id']),
  itemId: cellData.id,
  node: e.target as HTMLElement
});

const Honeycomb: FC<HoneycombPropsType> = ({
  items, valueKeyName = 'value', containerWidth: containerWidthProps, labelClassNames = '',
  numberOfCellsPerRow: numberOfCellsPerRowProps, cellBaseSize: cellBaseSizeProps, cellOffset = 5, idFn,
  cellLabelChildrenFn, popupContentFn, openPopupOn = 'hover', highlightedPropPath, onHoneycombClick,
  colors = ['green', 'yellow', 'orange', 'red', 'dark-red'], colorThresholds = [19, 39, 59, 79],
}) => {
  const popupOnHover = openPopupOn === 'hover';
  const popupOnClick = openPopupOn === 'click';

  const {
    ref: containerRef,
    width: containerWidth = containerWidthProps,
  } = useResizeDetector({handleHeight: false, handleWidth: true});

  const popupRef = useRef<PopupType>(null);

  const [popupData, setPopupData] = useState<PopupDataType | null>(null);

  const labels = useMemo(
    () => transform(
      items,
      (acc, item) => {
        acc[idFn(item)] = cellLabelChildrenFn(item);
      },
      []
    ),
    [items, idFn, cellLabelChildrenFn]
  );

  useEffect(
    () => {
      setPopupData(null);
    },
    [numberOfCellsPerRowProps, cellBaseSizeProps]
  );

  useEffect(() => {
    if (popupOnClick) {
      const onClickOutside = (e) => {
        // if popup is open, close on click on everything except honeycomb cell or popup itself
        if (
          popupData && containerRef.current && popupRef.current &&
          (!e.target.closest('polygon') && !popupRef.current?.elementRef.current?.contains(e.target))
        ) {
          setPopupData(null);
        }
      };

      window.addEventListener('click', onClickOutside);
      return () => {
        window.removeEventListener('click', onClickOutside);
      };
    }
  }, [popupData, containerRef, popupOnClick]);

  const width = containerWidthProps || containerWidth || 0;

  const {numberOfCellsPerRow, hexHeight, hexWidth, chartWidth} = useMemo(() => {
    let cellBaseSize = 0;
    let numberOfCellsPerRow = 0;
    let hexHeight = 0;
    let hexWidth = 0;
    let chartWidth = 0;
    if (items?.length !== 0 && width) {
      if (numberOfCellsPerRowProps) {
        numberOfCellsPerRow = min([numberOfCellsPerRowProps!, items.length]) || 0;
        let oneCellSpaceAvailable = 0;
        if (items.length < numberOfCellsPerRow * 2) {
          // only 1 row available or there're 2 rows, but the 2nd one is not full
          oneCellSpaceAvailable = (width - cellOffset * (numberOfCellsPerRow + 1)) / numberOfCellsPerRow;
        } else {
          oneCellSpaceAvailable = (width - cellOffset) / (numberOfCellsPerRow + 0.5) - cellOffset;
        }
        hexWidth = oneCellSpaceAvailable;
        hexHeight = (oneCellSpaceAvailable / Math.sqrt(3)) * 2;
        cellBaseSize = hexHeight;
      } else if (cellBaseSizeProps) {
        cellBaseSize = cellBaseSizeProps;
        hexHeight = cellBaseSize;
        hexWidth = Math.sqrt(3) * (cellBaseSize / 2);
        const cellsInFirstRow = Math.floor((width - cellOffset) / (hexWidth + cellOffset));
        if (items.length < cellsInFirstRow * 2) {
          // only 1 row available or there're 2 rows, but the 2nd one is not full
          numberOfCellsPerRow = min([cellsInFirstRow, items.length]) || 0;
        } else {
          numberOfCellsPerRow = Math.floor(
            (width - (hexWidth + cellOffset) / 2 - cellOffset) / (hexWidth + cellOffset)
          );
        }
      }

      const numberOfRows = Math.ceil(items.length / numberOfCellsPerRow);

      chartWidth = (hexWidth + cellOffset) * (numberOfRows === 1 ? items.length : numberOfCellsPerRow) +
        cellOffset +
        // if the number of items is enough to fill at least 2 rows entirely,
        // we add extra space as we have a shift for every other row
        (items.length < 2 * numberOfCellsPerRow ? 0 : (hexWidth + cellOffset) / 2);
    }
    return {numberOfCellsPerRow, hexHeight, hexWidth, chartWidth};
  }, [numberOfCellsPerRowProps, cellBaseSizeProps, cellOffset, width, items?.length]);

  useLayoutEffect(() => {
    const newValue = find(items, (item) => idFn(item) === popupData?.itemId);
    if (popupData && highlightedPropPath && popupData?.faded !== !get(newValue, highlightedPropPath)) {
      setPopupData({...popupData, faded: !get(newValue, highlightedPropPath)});
    }
  }, [highlightedPropPath, popupData, items, idFn]);

  const colorScaleFn = scaleThreshold({
    domain: colorThresholds,
    range: colors,
  });

  const getPopupOffset = useCallback(({reference, popper}) => {
    const isEnoughSpaceOnLeft = reference.x + reference.width / 2 - popper.width / 2 >= 0;
    const isEnoughSpaceOnRight = reference.x + reference.width / 2 + popper.width / 2 <= window.innerWidth;
    const isEnoughSpace = isEnoughSpaceOnLeft && isEnoughSpaceOnRight;

    return [
      isEnoughSpace ? 0 : (isEnoughSpaceOnLeft ? -SUIR_PADDING : SUIR_PADDING),
      cellOffset
    ];
  }, [cellOffset]);

  const calcRowTranslate = useCallback((rowId: number) => {
    return isUndefined(rowId) ? [0, 0] : [
      (rowId % 2 === 1 ? (hexWidth + cellOffset) / 2 : 0) + cellOffset,
      (hexHeight + cellOffset) * 0.75 * rowId,
    ];
  }, [hexWidth, hexHeight, cellOffset]);

  const calcCellTranslate = useCallback((cellId: number) => {
    return isUndefined(cellId) ? [0, 0] : [
      (hexWidth + cellOffset) * cellId,
      cellOffset,
    ];
  }, [hexWidth, cellOffset]);

  const onMouseOver = useCallback(
    (e, cellData: CellDataType) => {
      if (popupOnHover) {
        setPopupData(makePopupData(cellData, e));
      }
    },
    [popupOnHover]
  );

  const onMouseOut = useCallback(
    () => {
      if (popupOnHover) {
        setPopupData(null);
      }
    },
    [popupOnHover]
  );

  const onClick = useCallback(
    (e, cellData: CellDataType) => {
      const data: PopupDataType = makePopupData(cellData, e);
      if (popupOnClick) {
        setPopupData(data);
      }
      onHoneycombClick?.(data);
    },
    [onHoneycombClick, popupOnClick]
  );

  const rows = useMemo(
    () => chunk(items, numberOfCellsPerRow),
    [items, numberOfCellsPerRow]
  );

  if (!items || items?.length === 0) {
    return null;
  }

  const chartHeight = width ?
    hexHeight + 2 * cellOffset + (hexHeight + cellOffset) * 0.75 * (Math.ceil(items.length / numberOfCellsPerRow) - 1) :
    0;

  return (
    <div ref={containerRef} className='honeycomb-chart-container'>
      <svg height={chartHeight} width={chartWidth}>
        <Group>
          {chartWidth && chartHeight && map(
            rows,
            (rowItems, rowId) => {
              const rowTranslate = calcRowTranslate(rowId);
              return (
                <Group
                  key={rowId}
                  className='honeycomb-row'
                  transform={`translate(${rowTranslate[0]}, ${rowTranslate[1]})`}
                >
                  {map(rowItems, (item, cellId) => {
                    const id = idFn(item);
                    // a cell is styled as focused when user hovers over it or clicks on it to see a popup
                    const position = calcCellTranslate(cellId);
                    const color = colorScaleFn(item[valueKeyName]);
                    const faded = !!highlightedPropPath && !get(item, highlightedPropPath);

                    return (
                      <Hexagon
                        key={id}
                        itemId={id}
                        item={item}
                        x={position[0]}
                        y={position[1]}
                        width={hexWidth}
                        height={hexHeight}
                        faded={faded}
                        color={color}
                        cellOffset={cellOffset}
                        label={labels[id]}
                        labelClassNames={labelClassNames}
                        rowId={rowId}
                        cellId={cellId}
                        onMouseOver={onMouseOver}
                        onMouseOut={onMouseOut}
                        onClick={onClick}
                      />
                    );
                  })}
                </Group>
              );
            }
          )}
        </Group>
      </svg>
      {popupData &&
        <Popup
          key={popupData.itemId}
          ref={popupRef}
          open
          on={openPopupOn}
          position='top center'
          context={popupData.node}
          content={popupContentFn(popupData)}
          popperModifiers={[
            {
              name: 'flip',
              enabled: true,
              options: {
                fallbackPlacements: [
                  'top-start', 'top-end', 'bottom', 'bottom-start', 'bottom-end'
                ],
                flipVariations: true,
              },
            },
            {
              name: 'offset',
              enabled: true,
              options: {
                offset: getPopupOffset,
              }
            },
            syncPopperHideAttributes
          ]}
        />
      }
    </div>
  );
};

export default observer(Honeycomb);
